import { useEffect, useState } from 'react';
import { getLang } from '../i18n';
import { haptic } from '../telegram';

const STORAGE_KEY = 'cradle7.introSeen';

const STRINGS = {
  en: {
    'intro.line1': '// SIGNAL ACQUIRED',
    'intro.line2': '// CRADLE-7 // SECTOR Σ-9',
    'intro.title': 'CRADLE-7',
    'intro.subtitle': 'NINE LIVES IN THE DARK',
    'intro.tapToContinue': 'TAP ANYWHERE TO BEGIN',
  },
  ru: {
    'intro.line1': '// СИГНАЛ ПРИНЯТ',
    'intro.line2': '// КОЛЫБЕЛЬ-7 // СЕКТОР Σ-9',
    'intro.title': 'КОЛЫБЕЛЬ-7',
    'intro.subtitle': 'ДЕВЯТЬ ЖИЗНЕЙ ВО ТЬМЕ',
    'intro.tapToContinue': 'НАЖМИ ГДЕ-УГОДНО ЧТОБЫ НАЧАТЬ',
  },
} as const;

function s(key: keyof typeof STRINGS.en): string {
  const lang = getLang();
  return STRINGS[lang]?.[key] ?? STRINGS.en[key];
}

export function hasSeenIntro(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch { /* ignore */ }
}

type Phase = 'init' | 'line1' | 'line2' | 'title' | 'prompt';

export function Intro({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<Phase>('init');

  useEffect(() => {
    const timeline: Array<{ at: number; phase: Phase }> = [
      { at: 600,  phase: 'line1' },
      { at: 1000, phase: 'line2' },
      { at: 1800, phase: 'title' },
      { at: 2600, phase: 'prompt' },
    ];
    const timers = timeline.map((step) =>
      setTimeout(() => setPhase(step.phase), step.at),
    );
    const finish = setTimeout(() => {
      markSeen();
      onComplete();
    }, 3500);
    timers.push(finish);
    return () => { timers.forEach(clearTimeout); };
  }, [onComplete]);

  const handleSkip = () => {
    haptic.impact('medium');
    markSeen();
    onComplete();
  };

  return (
    <div
      onPointerDown={handleSkip}
      className="scanlines"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        zIndex: 999,
        cursor: 'pointer',
        overflow: 'hidden',
        padding: 'var(--space-6)',
      }}
    >
      <div className="scan-band" />

      {/* Line 1 — appears at phase line1 */}
      <div
        className="label glow-primary"
        style={{
          fontSize: '0.75rem',
          letterSpacing: '0.2em',
          opacity: phase === 'init' ? 0 : phase === 'line1' ? 1 : 0.5,
          transition: 'opacity 0.4s ease',
        }}
      >
        {phase !== 'init' && s('intro.line1')}
      </div>

      {/* Line 2 — appears at phase line2 */}
      <div
        className="label glow-secondary"
        style={{
          fontSize: '0.75rem',
          letterSpacing: '0.2em',
          opacity: phase === 'init' || phase === 'line1' ? 0 : phase === 'line2' ? 1 : 0.5,
          transition: 'opacity 0.4s ease',
        }}
      >
        {(phase === 'line2' || phase === 'title' || phase === 'prompt') && s('intro.line2')}
      </div>

      {/* Title — appears at phase title */}
      <div
        style={{
          opacity: phase === 'title' || phase === 'prompt' ? 1 : 0,
          transform: phase === 'title' || phase === 'prompt' ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.92)',
          transition: 'opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          textAlign: 'center',
          marginTop: 'var(--space-6)',
        }}
      >
        <h1
          className="headline pulse glow-primary"
          style={{
            fontSize: 'clamp(2.4rem, 10vw, 5rem)',
            letterSpacing: '0.12em',
            margin: 0,
          }}
        >
          {s('intro.title')}
        </h1>
        <div
          className="subheadline glow-secondary"
          style={{
            marginTop: 'var(--space-3)',
            fontSize: '0.85rem',
            letterSpacing: '0.2em',
            color: 'var(--fg-secondary)',
          }}
        >
          {s('intro.subtitle')}
        </div>
      </div>

      {/* Prompt — appears at phase prompt */}
      <div
        className="label blink"
        style={{
          marginTop: 'var(--space-6)',
          color: 'var(--fg-muted)',
          fontSize: '0.7rem',
          opacity: phase === 'prompt' ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      >
        {phase === 'prompt' && s('intro.tapToContinue')}
      </div>
    </div>
  );
}
